import * as React from 'react';
import {
  Controller,
  type Control,
  type FieldValues,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { FileUploadZone } from '../composites/FileUploadZone';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

export interface RhfFileUploadConfig {
  multiple: boolean;
  maxFiles: number | null;
  existingIdField?: string;
  existingNameField?: string;
  removedIdsField?: string;
}

export interface RhfFileUploadProps extends RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  accept?: string;
  maxSize?: number;
  fileType?: string;
  fileUpload: RhfFileUploadConfig;
  setValue: UseFormSetValue<FieldValues>;
}

function normalizeStagedFiles(value: unknown): File[] {
  if (value == null) return [];
  if (value instanceof File) return [value];
  if (Array.isArray(value)) {
    return value.filter((item): item is File => item instanceof File);
  }
  if (typeof value === 'object' && 'length' in value) {
    const list = value as FileList;
    return Array.from(list).filter((item): item is File => item instanceof File);
  }
  return [];
}

/** Build existing file entries from form field values. Supports both single (string)
 *  and multi-file (string[]) existing docs. */
function buildExistingFiles(
  existingId: unknown,
  existingName: unknown,
  removedIds: string[],
): Array<{ id: string; name: string }> {
  // Multi-file: both fields are arrays of matching length
  if (Array.isArray(existingId) && Array.isArray(existingName)) {
    return existingId
      .map((id, idx) => ({
        id: typeof id === 'string' ? id : '',
        name: typeof existingName[idx] === 'string' ? (existingName[idx] as string) : '',
      }))
      .filter((f) => f.id && f.name && !removedIds.includes(f.id));
  }

  // Single-file: both fields are strings
  const idStr = typeof existingId === 'string' ? existingId : '';
  const nameStr = typeof existingName === 'string' ? existingName : '';
  if (idStr && nameStr && !removedIds.includes(idStr)) {
    return [{ id: idStr, name: nameStr }];
  }

  return [];
}

export const RhfFileUpload = React.forwardRef<HTMLInputElement, RhfFileUploadProps>(
  function RhfFileUpload(
    {
      control,
      name,
      label,
      hint,
      tooltip,
      required = false,
      semanticId,
      disabled = false,
      accept,
      fileType,
      fileUpload,
      setValue,
      controlWidth,
    },
    ref,
  ) {
    const uploadConfig: RhfFileUploadConfig = {
      multiple: fileUpload?.multiple !== false,
      maxFiles: fileUpload?.maxFiles ?? null,
      existingIdField: fileUpload?.existingIdField,
      existingNameField: fileUpload?.existingNameField,
      removedIdsField: fileUpload?.removedIdsField,
    };

    const existingId = useWatch({
      control,
      name: uploadConfig.existingIdField ?? name,
      disabled: !uploadConfig.existingIdField,
    });
    const existingName = useWatch({
      control,
      name: uploadConfig.existingNameField ?? name,
      disabled: !uploadConfig.existingNameField,
    });
    const removedIdsRaw = useWatch({
      control,
      name: uploadConfig.removedIdsField ?? name,
      disabled: !uploadConfig.removedIdsField,
    });
    const removedIds = Array.isArray(removedIdsRaw)
      ? removedIdsRaw.filter((id): id is string => typeof id === 'string')
      : [];

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          const stagedFiles = normalizeStagedFiles(field.value);

          // Support both single-file (string) and multi-file (string[]) existing docs.
          const existingFiles = buildExistingFiles(existingId, existingName, removedIds);

          const handleAdd = (added: File[]): void => {
            if (added.length === 0) return;
            let next = uploadConfig.multiple
              ? [...stagedFiles, ...added]
              : added.slice(0, 1);
            if (uploadConfig.maxFiles != null) {
              const slotsAvailable = Math.max(0, uploadConfig.maxFiles - existingFiles.length);
              next = next.slice(0, slotsAvailable);
            }
            if (next.length === 0) return;
            field.onChange(next);
          };

          const handleRemoveStaged = (index: number): void => {
            field.onChange(stagedFiles.filter((_, i) => i !== index));
          };

          const handleRemoveExisting = (id: string): void => {
            if (!uploadConfig.removedIdsField) return;
            if (removedIds.includes(id)) return;
            setValue(uploadConfig.removedIdsField, [...removedIds, id], { shouldDirty: true });
          };

          return (
            <FormField
              label={label}
              hint={hint}
              tooltip={tooltip}
              required={required}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <FileUploadZone
                inputRef={composeRefs(field.ref, ref)}
                semanticId={semanticId}
                name={field.name}
                stagedFiles={stagedFiles}
                existingFiles={existingFiles}
                onAdd={handleAdd}
                onRemoveStaged={handleRemoveStaged}
                onRemoveExisting={
                  uploadConfig.removedIdsField ? handleRemoveExisting : undefined
                }
                multiple={uploadConfig.multiple}
                maxFiles={uploadConfig.maxFiles}
                accept={accept}
                fileType={fileType}
                disabled={disabled}
                error={Boolean(errorText)}
                onBlur={field.onBlur}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfFileUpload.displayName = 'RhfFileUpload';
